/**
 * Currency helpers for Number Field's declarative `currency` convenience.
 *
 * Both functions are pure and depend only on `Intl` — no data files, no
 * dependencies — so they are trivially unit-testable. They port NFE's currency
 * logic (`useCurrency.getSuggestedCurrencyPlacement` / `getCurrencySymbol`),
 * with the symbol derived from `Intl` instead of a static currency table.
 */

/**
 * Resolves which side a currency symbol belongs on for a locale/currency pair,
 * mirroring NFE's `getSuggestedCurrencyPlacement`: format a sample amount and
 * inspect the emitted parts. When the first part is the currency the symbol
 * leads (`'start'`, e.g. en-US `$1,111.10`); otherwise it trails (`'end'`, e.g.
 * de-DE `1.111,10 €`). Falls back to `'start'` if `Intl` throws on the inputs.
 */
export function resolveCurrencyPlacement(
  locale: string | string[] | undefined,
  currency: string,
): 'start' | 'end' {
  try {
    const parts = new Intl.NumberFormat(locale, { style: 'currency', currency }).formatToParts(1111.1)
    return parts[0]?.type === 'currency' ? 'start' : 'end'
  }
  catch {
    return 'start'
  }
}

/**
 * Derives the locale-aware currency symbol from `Intl`, replacing NFE's static
 * `supportedCurrencies.json` lookup. Formats zero with
 * `currencyDisplay: 'narrowSymbol'` and returns the `currency` part (e.g. `$`,
 * `€`, `kr`). Falls back to the raw ISO code when no symbol part is emitted or
 * `Intl` throws on the inputs.
 */
export function getCurrencySymbol(
  locale: string | string[] | undefined,
  currency: string,
): string {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0)
    const symbolPart = parts.find(part => part.type === 'currency')
    return symbolPart?.value ?? currency
  }
  catch {
    return currency
  }
}
